import React, { useState, useEffect, useRef, useMemo } from 'react'
import Dropdown from 'react-bootstrap/Dropdown'
import { Pill } from 'src/components/Reusable/Pill'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox'
import { Tooltip } from 'src/components/Reusable/Tooltip'
import { TooltipPositions } from 'src/components/Reusable/Tooltip/Tooltip'
import { StyledDropdownToggle } from '../../StandardDropdown/StandardDropdown.styles'
import { FlexRow } from '../../Flex'
import { PlaceholderText } from '../Inputs.styles'
import { StyledInputError } from '../TextInput/styles'

export interface TagInputOption {
  name: string | JSX.Element
  value: string
  icon?: React.ReactNode
}

export interface TagInputProps {
  options: TagInputOption[]
  onChange?: (value: string[]) => void
  initialValue?: string[]
  isDisabled?: boolean
  placeholder?: string
  errorMessage?: string
  overflowTooltipPlacement?: TooltipPositions
}

// Helper function to estimate pill width (including close button if applicable)
const estimatePillWidth = (
  text: string | JSX.Element,
  isCloseable: boolean,
): number => {
  const textStr = typeof text === 'string' ? text : ''
  // More conservative estimation: text width + padding (12px each side) + close button if applicable
  const textWidth = textStr.length * 8 // More conservative estimate to avoid overflow
  const padding = 24 // 12px on each side
  const closeButton = isCloseable ? 24 : 0
  return textWidth + padding + closeButton
}

export const TagInput = ({
  options,
  onChange,
  initialValue,
  isDisabled,
  placeholder = 'Select user',
  errorMessage,
  overflowTooltipPlacement = TooltipPositions.right,
}: TagInputProps): JSX.Element => {
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const [availableWidth, setAvailableWidth] = useState(300)

  useEffect(() => {
    if (initialValue) {
      setSelectedTags(initialValue)
    }
  }, [initialValue])

  // Measure container width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth
        // Use the full width, minus padding (typically 12px on each side)
        setAvailableWidth(Math.max(width - 24, 300))
      }
    }

    // Initial measurement with a small delay to ensure DOM is ready
    setTimeout(updateWidth, 0)
    const resizeObserver = new ResizeObserver(updateWidth)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [selectedTags]) // Re-measure when tags change

  const handleSelect = (tag: string) => {
    let newTags: string[]
    if (selectedTags.includes(tag)) {
      newTags = selectedTags.filter((t) => t !== tag)
    } else {
      newTags = [...selectedTags, tag]
    }
    setSelectedTags(newTags)
    onChange?.(newTags)
  }

  const handleOnClose = (e: React.MouseEvent<HTMLDivElement>, tag: string) => {
    e.preventDefault()
    e.stopPropagation()
    const newTags = selectedTags.filter((t) => t !== tag)
    setSelectedTags(newTags)
    onChange?.(newTags)
  }

  // Calculate visible tags based on available width
  const { visibleTags, hiddenTags } = useMemo(() => {
    if (selectedTags.length === 0) {
      return { visibleTags: [], hiddenTags: [] }
    }

    const getValueName = (value: string) => {
      return options.find((option) => option.value === value)?.name
    }

    const tagsWithNames = selectedTags.map((tag) => ({
      tag,
      name: getValueName(tag),
    }))

    let usedWidth = 0
    const visible: string[] = []
    const GAP_WIDTH = 8 // gap between pills
    const MORE_PILL_WIDTH = 90 // estimated width for "+ X more" pill (slightly more conservative)
    const SAFETY_MARGIN = 10 // Extra margin to prevent overflow

    for (let i = 0; i < tagsWithNames.length; i++) {
      const { tag, name } = tagsWithNames[i]
      const nameStr = typeof name === 'string' ? name : ''
      const pillWidth = estimatePillWidth(nameStr, !isDisabled)

      // Check if there are remaining tags after this one
      const remainingTags = tagsWithNames.length - i - 1
      const needsMorePill = remainingTags > 0

      // Calculate total width needed: current pill + gap + "more" pill if needed + safety margin
      const totalWidthNeeded =
        usedWidth +
        pillWidth +
        (needsMorePill ? GAP_WIDTH + MORE_PILL_WIDTH : 0) +
        SAFETY_MARGIN

      if (totalWidthNeeded <= availableWidth) {
        visible.push(tag)
        usedWidth += pillWidth + GAP_WIDTH
      } else {
        // This tag doesn't fit, stop here
        break
      }
    }

    const hidden = selectedTags.slice(visible.length)
    return { visibleTags: visible, hiddenTags: hidden }
  }, [selectedTags, availableWidth, options, isDisabled])

  const getOption = (value: string) => {
    return options.find((option) => option.value === value)
  }

  const getValueName = (value: string) => {
    return getOption(value)?.name
  }

  const getValueIcon = (value: string) => {
    return getOption(value)?.icon
  }

  const hiddenTagsNames = hiddenTags
    .map((tag) => getValueName(tag))
    .filter(Boolean)
    .join(', ')

  return (
    <Dropdown
      autoClose="outside"
      style={{
        width: '100%',
      }}
    >
      <Dropdown.Toggle
        as={StyledDropdownToggle}
        ref={containerRef}
        style={{
          height: '40px',
          width: '100%',
        }}
        disabled={isDisabled}
      >
        {selectedTags.length > 0 ? (
          <FlexRow gap={8} style={{ width: '100%', overflow: 'hidden' }}>
            {visibleTags.map((tag) => {
              const icon = getValueIcon(tag)
              const name = getValueName(tag)
              return (
                <Pill
                  key={tag}
                  label={
                    icon ? (
                      <FlexRow gap={4} style={{ alignItems: 'center' }}>
                        {icon}
                        {name}
                      </FlexRow>
                    ) : (
                      name
                    )
                  }
                  backgroundColor="#FFFFFF"
                  isCloseable={!isDisabled}
                  onClose={(e) => handleOnClose(e, tag)}
                  useBorder
                  $size="small"
                  closeIconSize="10px"
                />
              )
            })}
            {hiddenTags.length > 0 && (
              <Tooltip
                id={`more-tags-${selectedTags.join('-')}`}
                tooltipBody={hiddenTagsNames}
                placement={overflowTooltipPlacement}
              >
                <span onClick={(e) => e.stopPropagation()}>
                  <Pill
                    label={`+ ${hiddenTags.length} more`}
                    useBorder
                    fontSize="12px"
                  />
                </span>
              </Tooltip>
            )}
          </FlexRow>
        ) : (
          <PlaceholderText>{placeholder}</PlaceholderText>
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {options.map((option) => (
          <Dropdown.Item
            key={option.value}
            onClick={() => handleSelect(option.value)}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                pointerEvents: 'none',
              }}
            >
              <CheckBoxInput
                checked={selectedTags.includes(option.value)}
                readOnly
                style={{ marginRight: '8px' }}
              />
              {option.icon && (
                <span style={{ marginRight: '8px', display: 'flex' }}>
                  {option.icon}
                </span>
              )}
              {option.name}
            </div>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
      {errorMessage && (
        <StyledInputError showError={true}>{errorMessage}</StyledInputError>
      )}
    </Dropdown>
  )
}
